import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Plus } from 'lucide-react';
import { Card, Alert, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Connection Providers admin — full CRUD over app_connection_providers (DB rows,
 * seeded once from the code registry, then user-owned). Relational model: each
 * provider belongs to exactly one category (category_id — pick it here, and the
 * category's provider list everywhere else derives from it). What stays code-defined
 * is the live-test `probe` implementation (services/connections/testConnection.js) —
 * a row picks one by key, or "none". Field specs are edited as JSON (validated
 * server-side field-by-field) since they're a power-user surface: they drive both
 * connection validation and the dynamic credential forms.
 */
function apiError(ex) {
  return (ex.response && ex.response.data && ex.response.data.error) || ex.message;
}

const BLANK = {
  key: '', label: '', icon: '', description: '', category_id: '', probe: '',
  sort: 0, is_active: true, config_fields: '[]', credential_fields: '[]',
};

function parseFieldsColumn(value) {
  try {
    const v = JSON.parse(value || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function FieldSpecEditor({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm mb-1">{label} (JSON array of {'{name, label, type, required, maxLen?, options?, placeholder?}'})</label>
      <textarea
        className="w-full min-h-[110px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-xs font-mono"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        spellCheck={false}
      />
    </div>
  );
}

function ProviderForm({ editing, categories, probes, onSaved, onCancel }) {
  const { fetchApi } = useAppApi();
  const isNew = !editing.id;
  const [form, setForm] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async () => {
    setErr('');
    let configFields;
    let credentialFields;
    try {
      configFields = JSON.parse(form.config_fields || '[]');
      credentialFields = JSON.parse(form.credential_fields || '[]');
    } catch {
      setErr('Field specs must be valid JSON arrays');
      return;
    }
    setSaving(true);
    const data = {
      label: form.label, icon: form.icon || null, description: form.description || null,
      category_id: Number(form.category_id), probe: form.probe || null,
      sort: Number(form.sort) || 0, is_active: !!form.is_active,
      config_fields: configFields, credential_fields: credentialFields,
    };
    try {
      if (isNew) await fetchApi('connectionProviderCreate', { ...data, key: form.key });
      else await fetchApi('connectionProviderUpdate', { id: form.id, ...data });
      onSaved();
    } catch (ex) {
      setErr(apiError(ex));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="font-semibold">{isNew ? 'New provider' : `Edit "${editing.label}"`}</div>
      {err && <Alert variant="error" title="Could not save">{err}</Alert>}
      <div className="grid grid-cols-2 gap-3">
        <Input label="Key (slug, immutable after create)" value={form.key || ''} disabled={!isNew}
          placeholder="e.g. github-enterprise" onChange={(e) => setField('key', e.target.value)} />
        <Input label="Label" value={form.label || ''} onChange={(e) => setField('label', e.target.value)} />
        <div>
          <label className="block text-sm mb-1">Category</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={form.category_id || ''}
            onChange={(e) => setField('category_id', e.target.value)}
          >
            <option value="" disabled>Select a category…</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-sm mb-1">Live-test probe (code-defined)</label>
          <select
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={form.probe || ''}
            onChange={(e) => setField('probe', e.target.value)}
          >
            <option value="">None — no "Test connection"</option>
            {probes.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
        <Input label="Icon (lucide name)" value={form.icon || ''} onChange={(e) => setField('icon', e.target.value)} />
        <Input label="Sort" type="number" value={form.sort ?? 0} onChange={(e) => setField('sort', e.target.value)} />
        <div className="col-span-2">
          <Input label="Description" value={form.description || ''} onChange={(e) => setField('description', e.target.value)} />
        </div>
      </div>
      <FieldSpecEditor label="Config fields (non-secret, stored plaintext)" value={form.config_fields} onChange={(v) => setField('config_fields', v)} />
      <FieldSpecEditor label="Credential fields (secret, encrypted at rest)" value={form.credential_fields} onChange={(v) => setField('credential_fields', v)} />
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-1 text-sm">
          <input type="checkbox" checked={!!form.is_active} onChange={(e) => setField('is_active', e.target.checked)} /> Active
        </label>
        <div className="flex-1" />
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={save} disabled={saving || !form.label || !form.category_id || (isNew && !form.key)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Card>
  );
}

function providerColumns({ categoryName, onEdit, onDelete, nav }) {
  return [
    {
      key: 'label',
      label: 'Label',
      render: (p) => (
        <div>
          <button onClick={() => nav(`/app-connection-provider/${p.id}`)} className="font-medium text-primary-600 hover:underline text-left">{p.label}</button>
          <div className="text-secondary-500 text-xs max-w-xs truncate" title={p.description}>{p.description}</div>
        </div>
      ),
    },
    { key: 'key', label: 'Key', render: (p) => <span className="font-mono text-xs">{p.key}</span> },
    { key: 'category_id', label: 'Category', render: (p) => categoryName(p.category_id) },
    { key: 'probe', label: 'Probe', render: (p) => <span className="font-mono text-xs">{p.probe || '—'}</span> },
    {
      key: 'fields',
      label: 'Fields',
      sortable: false,
      searchable: false,
      render: (p) => (
        <span className="text-xs">
          {parseFieldsColumn(p.config_fields).length} config / {parseFieldsColumn(p.credential_fields).length} secret
        </span>
      ),
    },
    {
      key: 'is_active',
      label: 'Status',
      searchable: false,
      render: (p) => (
        <span className={`px-2 py-0.5 rounded text-xs ${p.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
          {p.is_active ? 'active' : 'disabled'}
        </span>
      ),
    },
    {
      key: 'row_actions',
      label: '',
      sortable: false,
      searchable: false,
      render: (p) => (
        <Can module="connections" action="author" scope="all">
          <div className="whitespace-nowrap text-right">
            <button type="button" title="Edit" onClick={() => onEdit(p)} className="p-1 text-primary-600 hover:bg-primary-50 rounded"><Pencil size={14} /></button>
            <button type="button" title="Delete" onClick={() => onDelete(p)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
          </div>
        </Can>
      ),
    },
  ];
}

function ProvidersTable({ providers, categories, onEdit, onDelete, loading, onReload, nav }) {
  const categoryName = (id) => (categories.find((c) => Number(c.id) === Number(id)) || {}).name || `#${id}`;
  const [category, setCategory] = useState('');
  const categoryOptions = [
    { label: 'All categories', value: '' },
    ...categories.map((c) => ({ label: c.name || c.key, value: String(c.id) })),
  ];
  const rows = category ? providers.filter((p) => String(p.category_id) === category) : providers;
  return (
    <DataTable
      data={rows}
      columns={providerColumns({ categoryName, onEdit, onDelete, nav })}
      loading={loading}
      emptyText="No providers yet — run db:sync to seed the built-ins."
      search={{ placeholder: 'Search providers…', keys: ['label', 'description', 'key', 'probe'] }}
      toolbar={(
        <div className="w-52">
          <Select options={categoryOptions} value={category} onChange={setCategory} placeholder="All categories" searchable />
        </div>
      )}
      onReload={onReload}
    />
  );
}

export default function ConnectionProvidersPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [probes, setProbes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = () => Promise.all([
    fetchApi('crud', { model: 'AppConnectionProvider', action: 'list', orderBy: { column: 'sort', direction: 'asc' }, limit: 200 })
      .then((res) => setProviders((Array.isArray(res) ? res : res?.data) || [])),
    fetchApi('crud', { model: 'AppConnectionCategory', action: 'list', orderBy: { column: 'sort', direction: 'asc' }, limit: 100 })
      .then((res) => setCategories((Array.isArray(res) ? res : res?.data) || [])),
    fetchApi('connectionProviders').then((spec) => setProbes(spec.probes || [])),
  ]);

  useEffect(() => {
    load().catch((ex) => setErr(apiError(ex))).finally(() => setLoading(false));
  }, []);

  const onSaved = () => { setEditing(null); setErr(''); load().catch((ex) => setErr(apiError(ex))); };
  const onEdit = (p) => setEditing({
    ...p,
    config_fields: JSON.stringify(parseFieldsColumn(p.config_fields), null, 2),
    credential_fields: JSON.stringify(parseFieldsColumn(p.credential_fields), null, 2),
  });
  const onDelete = async (p) => {
    if (!window.confirm(`Delete provider "${p.label}"? Connections using it block deletion.`)) return;
    try {
      await fetchApi('connectionProviderDelete', p.id);
      onSaved();
    } catch (ex) {
      setErr(apiError(ex));
    }
  };

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Connection Providers"
        subtitle={<>{providers.length} provider{providers.length === 1 ? '' : 's'} — each belongs to one category; its field specs drive the connection forms and validation.</>}
        actions={(
          <Can module="connections" action="author" scope="all">
            <Button size="sm" onClick={() => setEditing({ ...BLANK })}><Plus size={14} className="mr-1 inline" /> New provider</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Error">{err}</Alert>}
      {editing && <ProviderForm editing={editing} categories={categories} probes={probes} onSaved={onSaved} onCancel={() => setEditing(null)} />}
      <ProvidersTable
        providers={providers}
        categories={categories}
        onEdit={onEdit}
        onDelete={onDelete}
        loading={loading}
        onReload={() => load().catch((ex) => setErr(apiError(ex)))}
        nav={nav}
      />
    </PageShell>
  );
}
